import { Button } from "@aix/ui"
import { useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"
import { X, Edit2, Check, Calendar, Target, TrendingUp, Activity, Clock, CheckCircle2 } from "lucide-react"

// Helper function to format dates
const formatDate = (date: string | Date): string => {
  const d = typeof date === "string" ? new Date(date) : date
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

interface DraftPlanReviewModalProps {
  quarterlyPlanId: string
  coachId: string
  coachName: string
  onClose: () => void
  onAccepted: () => void
}

interface Phase {
  name: string
  weeks: string
  focus: string
  target_metrics?: string
  rationale?: string
}

interface Milestone {
  week: number
  description: string
  metric?: string
}

interface SuggestedTrackable {
  name: string
  promptText: string
  dataType: "number" | "boolean" | "text"
  unit?: string
  category: "body" | "work" | "reflection" | "relationship" | "business"
  checkInWindow: "morning" | "evening"
  isRequired: boolean
  fieldKey: string
}

export default function DraftPlanReviewModal({
  quarterlyPlanId,
  coachId,
  coachName,
  onClose,
  onAccepted,
}: DraftPlanReviewModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedPhases, setEditedPhases] = useState<Phase[]>([])
  const [editedMilestones, setEditedMilestones] = useState<Milestone[]>([])
  const [editedTrackables, setEditedTrackables] = useState<SuggestedTrackable[]>([])
  const [showWeekOnePrompt, setShowWeekOnePrompt] = useState(false)
  const [generateWeekOne, setGenerateWeekOne] = useState(true)

  // Load draft plan
  const { data: plan, isLoading } = trpc.plans.quarterlyPlans.get.useQuery({
    quarterlyPlanId,
  })

  // Mutations
  const updateMutation = trpc.plans.quarterlyPlans.update.useMutation({
    onSuccess: () => {
      toast.success("Changes saved")
      setIsEditing(false)
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save changes")
    },
  })

  const acceptMutation = trpc.plans.quarterlyPlans.acceptDraft.useMutation({
    onSuccess: (result) => {
      const trackablesCount = result.trackables?.length || 0
      if (result.weekOnePlan && trackablesCount > 0) {
        toast.success(`Quarterly plan activated! Week 1 is ready with ${trackablesCount} trackables.`)
      } else if (result.weekOnePlan) {
        toast.success("Quarterly plan activated! Week 1 is ready.")
      } else if (trackablesCount > 0) {
        toast.success(`Quarterly plan activated with ${trackablesCount} trackables!`)
      } else {
        toast.success("Quarterly plan activated!")
      }
      onAccepted()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to accept plan")
    },
  })

  const deleteMutation = trpc.plans.quarterlyPlans.delete.useMutation({
    onSuccess: () => {
      toast.success("Draft plan discarded")
      onClose()
    },
    onError: (error) => {
      toast.error(error.message || "Failed to discard plan")
    },
  })

  // Initialize edited state when plan loads
  if (plan && editedPhases.length === 0) {
    setEditedPhases(plan.phases as Phase[])
    setEditedMilestones((plan.milestones as Milestone[]) || [])
    setEditedTrackables((plan.suggestedTrackables as SuggestedTrackable[]) || [])
  }

  const handleSaveEdits = () => {
    updateMutation.mutate({
      quarterlyPlanId,
      phases: editedPhases,
      milestones: editedMilestones,
    })
  }

  const handleAcceptClick = () => {
    // Show Week 1 prompt instead of accepting immediately
    setShowWeekOnePrompt(true)
  }

  const handleConfirmAccept = () => {
    acceptMutation.mutate({
      quarterlyPlanId,
      generateWeekOne,
    })
  }

  const handleDiscard = () => {
    if (confirm("Are you sure you want to discard this draft plan? This cannot be undone.")) {
      deleteMutation.mutate({ quarterlyPlanId })
    }
  }

  if (isLoading || !plan) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  const phases = isEditing ? editedPhases : (plan.phases as Phase[])
  const milestones = isEditing ? editedMilestones : (plan.milestones as Milestone[]) || []
  const trackables = (plan.suggestedTrackables as SuggestedTrackable[]) || []

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-4 md:p-6 rounded-t-lg z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-semibold">Review Your {plan.durationWeeks}-Week Plan</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Generated from your intake conversation with {coachName}. Edit any section before accepting.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors ml-4">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-6">
          {/* Plan Overview */}
          <div className="bg-secondary p-4 rounded-lg border border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="ml-2 font-medium">{plan.durationWeeks} weeks</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                <div>
                  <span className="text-muted-foreground">Start:</span>
                  <span className="ml-2 font-medium">{formatDate(plan.startDate)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <div>
                  <span className="text-muted-foreground">End:</span>
                  <span className="ml-2 font-medium">{formatDate(plan.endDate)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Phases */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Plan Phases</h3>
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  leftIcon={<Edit2 className="w-4 h-4" />}
                >
                  Edit
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {phases.map((phase, idx) => (
                <div key={idx} className="bg-background border border-border rounded-lg p-4">
                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={phase.name}
                        onChange={(e) => {
                          const newPhases = [...editedPhases]
                          newPhases[idx] = { ...phase, name: e.target.value }
                          setEditedPhases(newPhases)
                        }}
                        className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-semibold"
                        placeholder="Phase name"
                      />
                      <input
                        type="text"
                        value={phase.weeks}
                        onChange={(e) => {
                          const newPhases = [...editedPhases]
                          newPhases[idx] = { ...phase, weeks: e.target.value }
                          setEditedPhases(newPhases)
                        }}
                        className="w-full bg-secondary border border-border rounded px-3 py-1.5 text-xs"
                        placeholder="Weeks (e.g., 1-4)"
                      />
                      <textarea
                        value={phase.focus}
                        onChange={(e) => {
                          const newPhases = [...editedPhases]
                          newPhases[idx] = { ...phase, focus: e.target.value }
                          setEditedPhases(newPhases)
                        }}
                        className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm resize-none"
                        rows={2}
                        placeholder="Phase focus"
                      />
                      <textarea
                        value={phase.target_metrics || ""}
                        onChange={(e) => {
                          const newPhases = [...editedPhases]
                          newPhases[idx] = { ...phase, target_metrics: e.target.value }
                          setEditedPhases(newPhases)
                        }}
                        className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm resize-none"
                        rows={1}
                        placeholder="Target metrics (optional)"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold">{phase.name}</h4>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">{phase.weeks}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{phase.focus}</p>
                      {phase.target_metrics && (
                        <div className="text-xs bg-secondary p-2 rounded">
                          <span className="font-medium">Target: </span>
                          {phase.target_metrics}
                        </div>
                      )}
                      {phase.rationale && (
                        <div className="text-xs text-muted-foreground mt-2 italic">{phase.rationale}</div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {isEditing && (
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={handleSaveEdits}
                  loading={updateMutation.isLoading}
                  leftIcon={<Check className="w-4 h-4" />}
                >
                  Save Changes
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditedPhases(plan.phases as Phase[])
                    setEditedMilestones((plan.milestones as Milestone[]) || [])
                    setIsEditing(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Milestones */}
          {milestones.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Key Milestones</h3>
              <div className="space-y-2">
                {milestones.map((milestone, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-background border border-border rounded-lg">
                    <div className="text-primary font-semibold min-w-[60px] text-sm">Week {milestone.week}</div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{milestone.description}</div>
                      {milestone.metric && (
                        <div className="text-xs text-muted-foreground mt-1">Target: {milestone.metric}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trackables */}
          {trackables.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Daily Trackables</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                These metrics will be created for daily tracking. You can customize them later.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {trackables.map((trackable, idx) => (
                  <div key={idx} className="bg-background border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{trackable.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{trackable.promptText}</div>
                      </div>
                      {trackable.isRequired && (
                        <span title="Required">
                          <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <div className="flex items-center gap-1 bg-secondary px-2 py-1 rounded">
                        <Clock className="w-3 h-3" />
                        <span className="capitalize">{trackable.checkInWindow}</span>
                      </div>
                      <div className="bg-secondary px-2 py-1 rounded capitalize">
                        {trackable.dataType}
                        {trackable.unit && ` (${trackable.unit})`}
                      </div>
                      <div className="bg-secondary px-2 py-1 rounded capitalize text-muted-foreground">
                        {trackable.category}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-muted-foreground bg-secondary p-3 rounded-lg">
                💡 <strong>Note:</strong> {trackables.filter((t) => t.isRequired).length} required trackable
                {trackables.filter((t) => t.isRequired).length !== 1 ? "s" : ""} will be added to your daily check-ins.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background border-t border-border p-4 md:p-6 rounded-b-lg flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button variant="ghost" onClick={handleDiscard} disabled={deleteMutation.isLoading}>
            Discard Plan
          </Button>
          <Button
            onClick={handleAcceptClick}
            loading={acceptMutation.isLoading}
            disabled={isEditing}
            className="bg-primary text-primary-foreground"
            leftIcon={<Check className="w-4 h-4" />}
          >
            Accept & Activate Plan
          </Button>
        </div>

        {/* Week 1 Generation Prompt */}
        {showWeekOnePrompt && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50 rounded-lg">
            <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-semibold mb-2">Ready to start Week 1?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                I can generate your Week 1 action plan right now based on the first phase of your quarterly plan.
                You&apos;ll be able to start tracking immediately!
              </p>

              <div className="bg-secondary p-4 rounded-lg mb-4 border border-border">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={generateWeekOne}
                    onChange={(e) => setGenerateWeekOne(e.target.checked)}
                    className="mt-1"
                    id="generate-week-one"
                  />
                  <label htmlFor="generate-week-one" className="text-sm cursor-pointer flex-1">
                    <span className="font-medium">Generate Week 1 plan now</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Creates actionable tasks for this week based on {phases[0]?.name || "your first phase"}
                    </p>
                  </label>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={handleConfirmAccept}
                  loading={acceptMutation.isLoading}
                  className="bg-primary text-primary-foreground flex-1"
                  leftIcon={<Check className="w-4 h-4" />}
                >
                  {generateWeekOne ? "Generate Week 1 & Activate" : "Activate Plan"}
                </Button>
                <Button variant="ghost" onClick={() => setShowWeekOnePrompt(false)} disabled={acceptMutation.isLoading}>
                  Back
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
